// ProfitReport.jsx
import React, { useState, useMemo } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Label,
  Form,
  Button,
  FormGroup,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { post } from "../../helpers/api_helper";
import { LEDGER_REPORT } from "../../helpers/url_helper";
import { useForm, Controller } from "react-hook-form";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import toastr from "toastr";
import TableContainer from "../../components/Common/TableContainer";

const ProfitReport = () => {
  /* ---------- 📅 helpers ---------- */
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  /* ---------- 🏷 breadcrumb ---------- */
  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "Profit Report", link: "#" },
  ];

  /* ---------- 📝 react‑hook‑form ---------- */
  const { handleSubmit, control, setValue, watch } = useForm({
    defaultValues: {
      start_date: oneMonthAgo.toISOString().split("T")[0],
      end_date: today.toISOString().split("T")[0],
    },
  });

  /* ---------- 🗃 table state ---------- */
  const [reportRows, setReportRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  /* ---------- 📊 table columns ---------- */
  const columns = useMemo(
    () => [
      {
        Header: "Customer",
        accessor: (row) => `${row.name} (${row.phone})`,
        disableFilters: true,
        filterable: false,
      },
      {
        Header: "Total Sell",
        accessor: "total_sell",
        Cell: ({ value }) => Number(value).toFixed(2),
        disableFilters: true,
        filterable: false,
      },
      {
        Header: "Total Profit",
        accessor: "total_profit",
        Cell: ({ value }) => Number(value).toFixed(2),
        disableFilters: true,
        filterable: false,
      },
      {
        Header: "Total Loss",
        accessor: "total_loss",
        Cell: ({ value }) => Number(value).toFixed(2),
        disableFilters: true,
        filterable: false,
      },
      {
        Header: "Net Profit",
        accessor: "net_profit",
        Cell: ({ value }) => Number(value).toFixed(2),
        disableFilters: true,
        filterable: false,
      },
    ],
    []
  );

  /* ---------- 🚀 fetch report ---------- */
  const fetchReport = async (formData) => {
    try {
      setLoading(true);

      // Guard: ensure at least 30‑day span
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const diffDays = (end - start) / (1000 * 60 * 60 * 24);
      if (diffDays < 30) {
        end.setDate(start.getDate() + 30);
        toastr.warning("End‑date moved to ensure minimum one‑month range");
        setValue("end_date", end.toISOString().split("T")[0]);
      }

      const payload = {
        // no customer_ul_id → backend returns every customer
        start_date: formData.start_date,
        end_date: formData.end_date,
        report_type: "profit_report",
      };

      const { data } = await post(LEDGER_REPORT, payload);
      setReportRows(data || []);
      toastr.success(`Loaded ${data.length} customer records`);
      setPageIndex(1); // reset table to first page
    } catch (err) {
      console.error(err);
      toastr.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const startDateWatch = watch("start_date");

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Profit Report"
            breadcrumbItems={breadcrumbItems}
          />

          {/* ---------- 🔎 filter form ---------- */}
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(fetchReport)}>
                <Row className="align-items-end">
                  <Col md={2}>
                    <Label>Start Date</Label>
                    <Controller
                      name="start_date"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Flatpickr
                          {...field}
                          className="form-control"
                          options={{
                            dateFormat: "Y-m-d",
                            maxDate: "today",
                            defaultDate: firstDayOfMonth,
                          }}
                        />
                      )}
                    />
                  </Col>

                  <Col md={2}>
                    <Label>End Date</Label>
                    <Controller
                      name="end_date"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Flatpickr
                          {...field}
                          className="form-control"
                          options={{
                            dateFormat: "Y-m-d",
                            minDate: startDateWatch,
                            maxDate: "today",
                            defaultDate: today,
                          }}
                        />
                      )}
                    />
                  </Col>

                  <Col md={2}>
                      <Button
                        color="primary"
                        type="submit"
                        className="w-100"
                        disabled={loading}
                      >
                        {loading ? "Generating..." : "Generate"}
                      </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>

          {/* ---------- 📋 results table ---------- */}
          {reportRows.length > 0 && (
            <Card>
              <CardBody>
                <TableContainer
                  columns={columns}
                  data={reportRows}
                  isPagination
                  isSortable
                  customPageSize={pageSize}
                  isAddOptions={false}
                  iscustomPageSize={false}
                  isBordered={false}
                  controlledPagination={{
                    pageIndex,
                    setPageIndex,
                    pageSize,
                    setPageSize,
                    totalPages: Math.ceil(reportRows.length / pageSize),
                  }}
                  isPagselection={false}
                />
              </CardBody>
            </Card>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProfitReport;
